// IBIC Categories - ICF-based classification codes
// Source: International Classification of Functioning (ICF)

export const IBIC_CATEGORIES = [
    // d1 Learning and Applying Knowledge
    "d1 Learning and applying knowledge",
    "d134 Finding out information",
    "d155 Acquiring skills",
    "d160 Focusing attention",
    "d175 Solving problems",
    "d177 Making decisions",
    // d2 General Tasks and Demands
    "d2 General tasks and demands",
    "d210 Carrying out a single task",
    "d230 Carrying out daily routine",
    "d240 Handling stress and other psychological demands",
    "d298A Managing one's behavior",
    // d3 Communication
    "d3 Communication",
    "d310 Receiving spoken messages",
    "d315 Receiving nonverbal messages",
    "d320 Receiving sign-language messages",
    "d325 Receiving written messages",
    "d330 Speaking",
    "d331 Preverbal vocalization",
    "d335 Expressing oneself with nonverbal messages",
    "d340 Expressing oneself with sign language",
    "d345 Writing messages",
    "d350 Conversing",
    "d360 Using communication devices and techniques",
    // d4 Mobility
    "d4 Mobility",
    "d410 Changing body position",
    "d415 Maintaining body position",
    "d420 Transferring oneself",
    "d430 Lifting and carrying objects",
    "d440 Fine hand use",
    "d450 Walking",
    "d451 Walking on stairs",
    "d455 Moving around in different ways",
    "d460 Moving around in different locations",
    "d470 Using transportation",
    // d5 Self-care
    "d5 Self-care",
    "d510 Washing oneself",
    "d520 Caring for body parts",
    "d530 Toileting",
    "d540 Dressing",
    "d550 Eating",
    "d560 Drinking",
    "d570 Caring for one's health",
    "d598A Ensuring one's own safety",
    // d6 Domestic Life
    "d6 Domestic life",
    "d610 Acquiring a place to live",
    "d620 Acquiring goods and services",
    "d630 Preparing meals",
    "d640 Doing housework",
    "d6400 Washing and drying clothes (by hand)",
    "d6401 Cleaning kitchen areas and utensils",
    "d6402 Cleaning living areas",
    "d6403 Using household appliances",
    "d6404 Storing daily necessities",
    "d6405 Disposing of household waste",
    "d6407 Making sleeping areas",
    "d6408A Washing and drying clothes with appliances",
    "d650 Caring for household objects",
    "d660 Assisting others",
    // d7 Interpersonal Interactions and Relationships
    "d7 Interpersonal interactions and relationships",
    "d710 Basic interpersonal interactions",
    "d720 Maintaining and managing interactions",
    "d730 Interacting with strangers",
    "d740 Formal relationships",
    "d750 Informal social relationships",
    "d760 Family relationships",
    "d770 Intimate relationships",
    // d8 Education, Work and Economic Life
    "d8 Education, work and economic life",
    "d839 Engaging in education, unspecified",
    "d859 Engaging in work and employment, unspecified",
    "d860 Basic economic transactions",
    "d865 Complex economic transactions",
    "d870 Economic self-sufficiency",
    // d9 Community, Social and Civic Life
    "d9 Community, social and civic life",
    "d910 Community life",
    "d920 Recreation and leisure",
    "d930 Religion and spirituality",
    "d950 Political life and citizenship",
    // b Body Functions
    "b Body functions",
    "b1528A Sense of safety",
    // e Environmental Factors
    "e Environmental factors",
    "e398A Personal support from informal caregiver",
    "e598A Public services & legislation – perceived quality",
    "e598B Public services & legislation – perceived participation"
] as const;

export type IbicCategory = typeof IBIC_CATEGORIES[number];

// Helper to extract code from category string (e.g., "d550" from "d550 Eating")
export const extractIbicCode = (category: string): string => {
    const match = category.match(/^([a-zA-Z]\d+[A-Z]?)/);
    return match ? match[1] : category.split(" ")[0];
};

// Get the group prefix from a category (e.g., "d5" from "d550 Eating")
export const getIbicGroupPrefix = (category: string): string => {
    const code = extractIbicCode(category);
    // Match the letter and first digit (e.g., "d5", "d6", "b", "e")
    const match = code.match(/^([a-zA-Z]\d?)/);
    return match ? match[1].toLowerCase() : "";
};

// Check if a category is a group header (e.g., "d5 Self-care" vs "d550 Eating")
export const isGroupHeader = (category: string): boolean => {
    const code = extractIbicCode(category);
    // Group headers have only 1-2 characters (e.g., "d5", "b", "e")
    return /^[a-zA-Z]\d?$/.test(code);
};

// IBIC Groups structure for accordion display
export interface IbicGroup {
    key: string;
    label: string;
    header: string;
    items: string[];
}

// Build grouped structure from flat array
export const getIbicGroups = (): IbicGroup[] => {
    const groups: IbicGroup[] = [];
    let currentGroup: IbicGroup | null = null;

    for (const category of IBIC_CATEGORIES) {
        if (isGroupHeader(category)) {
            // This is a group header, start a new group
            const prefix = getIbicGroupPrefix(category);
            currentGroup = {
                key: prefix,
                label: category.replace(/^[a-zA-Z]\d?\s+/, ""), // Remove code prefix
                header: category,
                items: []
            };
            groups.push(currentGroup);
        } else if (currentGroup) {
            // This is a sub-item, add to current group
            currentGroup.items.push(category);
        }
    }

    return groups;
};

// Pre-computed groups for performance
export const IBIC_GROUPS = getIbicGroups();

// Group labels for display (translatable keys)
export const IBIC_GROUP_LABELS: Record<string, string> = {
    d1: "Learning and applying knowledge",
    d2: "General tasks and demands",
    d3: "Communication",
    d4: "Mobility",
    d5: "Self-care",
    d6: "Domestic life",
    d7: "Interpersonal interactions and relationships",
    d8: "Education, work and economic life",
    d9: "Community, social and civic life",
    b: "Body functions",
    e: "Environmental factors"
};

// Swedish translations map (English -> Swedish)
export const IBIC_CATEGORIES_SV: Record<string, string> = {
    // d1 Learning and Applying Knowledge
    "d1 Learning and applying knowledge": "d1 Lärande och att tillämpa kunskap",
    "d134 Finding out information": "d134 Inhämta information",
    "d155 Acquiring skills": "d155 Tillägna sig färdigheter",
    "d160 Focusing attention": "d160 Fokusera uppmärksamhet",
    "d175 Solving problems": "d175 Lösa problem",
    "d177 Making decisions": "d177 Fatta beslut",
    // d2 General Tasks and Demands
    "d2 General tasks and demands": "d2 Allmänna uppgifter och krav",
    "d210 Carrying out a single task": "d210 Utföra en enskild uppgift",
    "d230 Carrying out daily routine": "d230 Utföra daglig rutin",
    "d240 Handling stress and other psychological demands": "d240 Hantera stress och andra psykiska krav",
    "d298A Managing one's behavior": "d298A Hantera sitt beteende",
    // d3 Communication
    "d3 Communication": "d3 Kommunikation",
    "d310 Receiving spoken messages": "d310 Ta emot talade meddelanden",
    "d315 Receiving nonverbal messages": "d315 Ta emot icke-verbala meddelanden",
    "d320 Receiving sign-language messages": "d320 Ta emot meddelanden på teckenspråk",
    "d325 Receiving written messages": "d325 Ta emot skrivna meddelanden",
    "d330 Speaking": "d330 Tala",
    "d331 Preverbal vocalization": "d331 Preverbal vokalisering",
    "d335 Expressing oneself with nonverbal messages": "d335 Uttrycka sig med icke-verbala meddelanden",
    "d340 Expressing oneself with sign language": "d340 Uttrycka sig med teckenspråk",
    "d345 Writing messages": "d345 Skriva meddelanden",
    "d350 Conversing": "d350 Konversera",
    "d360 Using communication devices and techniques": "d360 Använda kommunikationshjälpmedel och tekniker",
    // d4 Mobility
    "d4 Mobility": "d4 Förflyttning",
    "d410 Changing body position": "d410 Ändra kroppsställning",
    "d415 Maintaining body position": "d415 Bibehålla kroppsställning",
    "d420 Transferring oneself": "d420 Förflyttra sig",
    "d430 Lifting and carrying objects": "d430 Lyfta och bära föremål",
    "d440 Fine hand use": "d440 Finmotorik i händerna",
    "d450 Walking": "d450 Gå",
    "d451 Walking on stairs": "d451 Gå i trappor",
    "d455 Moving around in different ways": "d455 Förflytta sig på olika sätt",
    "d460 Moving around in different locations": "d460 Förflytta sig på olika platser",
    "d470 Using transportation": "d470 Använda transportmedel",
    // d5 Self-care
    "d5 Self-care": "d5 Personlig vård",
    "d510 Washing oneself": "d510 Tvätta sig",
    "d520 Caring for body parts": "d520 Vårda kroppsdelar",
    "d530 Toileting": "d530 Toalettbesök",
    "d540 Dressing": "d540 Klä på sig",
    "d550 Eating": "d550 Äta",
    "d560 Drinking": "d560 Dricka",
    "d570 Caring for one's health": "d570 Vårda sin hälsa",
    "d598A Ensuring one's own safety": "d598A Säkerställa sin egen säkerhet",
    // d6 Domestic Life
    "d6 Domestic life": "d6 Hemliv",
    "d610 Acquiring a place to live": "d610 Skaffa bostad",
    "d620 Acquiring goods and services": "d620 Skaffa varor och tjänster",
    "d630 Preparing meals": "d630 Förbereda måltider",
    "d640 Doing housework": "d640 Utföra hushållsarbete",
    "d6400 Washing and drying clothes (by hand)": "d6400 Tvätta och torka kläder (för hand)",
    "d6401 Cleaning kitchen areas and utensils": "d6401 Rengöra kök och redskap",
    "d6402 Cleaning living areas": "d6402 Rengöra bostaden",
    "d6403 Using household appliances": "d6403 Använda hushållsapparater",
    "d6404 Storing daily necessities": "d6404 Förvara dagliga förnödenheter",
    "d6405 Disposing of household waste": "d6405 Göra sig av med hushållsavfall",
    "d6407 Making sleeping areas": "d6407 Bädda sovplatser",
    "d6408A Washing and drying clothes with appliances": "d6408A Tvätta och torka kläder med apparater",
    "d650 Caring for household objects": "d650 Vårda hushållsföremål",
    "d660 Assisting others": "d660 Hjälpa andra",
    // d7 Interpersonal Interactions and Relationships
    "d7 Interpersonal interactions and relationships": "d7 Mellanmänskliga samspel och relationer",
    "d710 Basic interpersonal interactions": "d710 Grundläggande mellanmänskliga interaktioner",
    "d720 Maintaining and managing interactions": "d720 Upprätthålla och hantera interaktioner",
    "d730 Interacting with strangers": "d730 Interagera med främlingar",
    "d740 Formal relationships": "d740 Formella relationer",
    "d750 Informal social relationships": "d750 Informella sociala relationer",
    "d760 Family relationships": "d760 Familjerelationer",
    "d770 Intimate relationships": "d770 Intima relationer",
    // d8 Education, Work and Economic Life
    "d8 Education, work and economic life": "d8 Utbildning, arbete och ekonomiskt liv",
    "d839 Engaging in education, unspecified": "d839 Delta i utbildning, ospecificerat",
    "d859 Engaging in work and employment, unspecified": "d859 Delta i arbete och anställning, ospecificerat",
    "d860 Basic economic transactions": "d860 Grundläggande ekonomiska transaktioner",
    "d865 Complex economic transactions": "d865 Komplexa ekonomiska transaktioner",
    "d870 Economic self-sufficiency": "d870 Ekonomisk självförsörjning",
    // d9 Community, Social and Civic Life
    "d9 Community, social and civic life": "d9 Samhällsgemenskap, socialt och medborgerligt liv",
    "d910 Community life": "d910 Samhällsliv",
    "d920 Recreation and leisure": "d920 Rekreation och fritid",
    "d930 Religion and spirituality": "d930 Religion och andlighet",
    "d950 Political life and citizenship": "d950 Politiskt liv och medborgarskap",
    // b Body Functions
    "b Body functions": "b Kroppsfunktioner",
    "b1528A Sense of safety": "b1528A Känsla av trygghet",
    // e Environmental Factors
    "e Environmental factors": "e Omgivningsfaktorer",
    "e398A Personal support from informal caregiver": "e398A Personligt stöd från informell vårdgivare",
    "e598A Public services & legislation – perceived quality": "e598A Offentliga tjänster och lagstiftning - upplevd kvalitet",
    "e598B Public services & legislation – perceived participation": "e598B Offentliga tjänster och lagstiftning - upplevd delaktighet"
};

// Build reverse map (Swedish -> English) for parsing
export const IBIC_SV_TO_EN: Record<string, string> = Object.entries(IBIC_CATEGORIES_SV).reduce(
    (acc, [en, sv]) => {
        acc[sv] = en;
        return acc;
    },
    {} as Record<string, string>
);

// Helper to find English category key from either language
export const findIbicCategoryKey = (value: string): string | undefined => {
    // Check if it's already an English category
    if (IBIC_CATEGORIES.includes(value as IbicCategory)) {
        return value;
    }
    // Check if it's a Swedish category
    if (IBIC_SV_TO_EN[value]) {
        return IBIC_SV_TO_EN[value];
    }
    // Try to match by code prefix (e.g., "d550" from "d550 (Äta)")
    const codeMatch = value.match(/^([a-zA-Z]\d+[A-Z]?)/);
    if (codeMatch) {
        const code = codeMatch[1];
        const foundCategory = IBIC_CATEGORIES.find(cat => cat.startsWith(code + " "));
        if (foundCategory) return foundCategory;
    }
    return undefined;
};

// Helper to translate English category to Swedish
export const translateToSwedish = (englishCategory: string): string => {
    return IBIC_CATEGORIES_SV[englishCategory] || englishCategory;
};

// Helper to translate Swedish category to English
export const translateToEnglish = (swedishCategory: string): string => {
    return IBIC_SV_TO_EN[swedishCategory] || swedishCategory;
};
